import { Outlet, NavLink } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import useStoreBranding from '@/hooks/useStoreBranding';
import { LayoutDashboard, ShoppingCart, GraduationCap, Crown, FileText, Share2, LogOut, Menu, X, CalendarClock, Zap, Heart, MapPin, FileSignature, MessageCircle, Download, User, Store, Gift } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState } from 'react';
import MobileBottomNav from '@/components/MobileBottomNav';
import { DEFAULT_STORE_ACCENT, DEFAULT_STORE_PRIMARY } from '@/lib/storeTheme';

const CUSTOMER_BOTTOM_NAV = [
  { to: '/my', icon: LayoutDashboard, label: 'Beranda', end: true },
  { to: '/my/orders', icon: ShoppingCart, label: 'Pesanan' },
  { to: '/my/reseller', icon: Store, label: 'Reseller' },
  { to: '/my/memberships', icon: Crown, label: 'Member' },
  { to: '/my/profile', icon: User, label: 'Profil' },
];

const navItems = [
  { to: '/my', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { to: '/my/profile', icon: User, label: 'Profil Saya' },
  { to: '/my/orders', icon: ShoppingCart, label: 'Pesanan' },
  { to: '/my/invoices', icon: FileText, label: 'Invoice' },
  { to: '/my/payment-schedules', icon: CalendarClock, label: 'Jadwal Bayar' },
  { to: '/my/courses', icon: GraduationCap, label: 'Kursus' },
  { to: '/my/downloads', icon: Download, label: 'Download' },
  { to: '/my/memberships', icon: Crown, label: 'Membership' },
  { to: '/my/loyalty', icon: Gift, label: 'Loyalty & Reward' },
  { to: '/my/affiliate', icon: Share2, label: 'Affiliate' },
  { to: '/my/reseller', icon: Store, label: 'Reseller' },
  { to: '/my/ppob', icon: Zap, label: 'PPOB' },
  { to: '/my/donations', icon: Heart, label: 'Donasi' },
  { to: '/my/contracts', icon: FileSignature, label: 'Kontrak' },
  { to: '/my/addresses', icon: MapPin, label: 'Alamat' },
  { to: '/my/wishlist', icon: Heart, label: 'Wishlist' },
  { to: '/my/support', icon: MessageCircle, label: 'Bantuan' },
];

export default function CustomerLayout() {
  const { user, logout } = useAuth();
  const { theme } = useStoreBranding({ mode: 'customer' });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const primary = theme?.primary || DEFAULT_STORE_PRIMARY;
  const accent = theme?.accent || DEFAULT_STORE_ACCENT;
  const logoUrl = theme?.logoUrl;
  const brandName = theme?.brandName || 'Member Area';

  return (
    <div className="flex h-screen bg-slate-50 customer-area" data-testid="customer-layout">
      {sidebarOpen && <div className="fixed inset-0 bg-black/30 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 flex flex-col transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-6 border-b border-slate-200">
          {logoUrl ? (
            <img src={logoUrl} alt={brandName} className="h-10 max-w-[180px] object-contain" />
          ) : (
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white" style={{ backgroundColor: primary }}>
                <Store size={18} />
              </div>
              <span className="font-bold text-slate-900 truncate">{brandName}</span>
            </div>
          )}
          <p className="text-xs text-slate-500 mt-1">Member Area</p>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map(({ to, icon: Icon, label, end }) => (
            <NavLink key={to} to={to} end={end} onClick={() => setSidebarOpen(false)}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${isActive ? '' : 'text-slate-600 hover:bg-slate-100'}`}
              style={({ isActive }) => isActive ? { backgroundColor: `${primary}1a`, color: primary } : undefined}>
              <Icon size={18} />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold" style={{ backgroundColor: accent }}>
              {user?.name?.[0] || 'C'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" className="w-full mt-2 text-slate-500 hover:text-red-600" onClick={logout} data-testid="customer-logout-btn">
            <LogOut size={16} className="mr-2" /> Keluar
          </Button>
        </div>
      </aside>
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 bg-white/80 backdrop-blur-md border-b border-slate-200 flex items-center px-4 lg:px-6 sticky top-0 z-30">
          <Button variant="ghost" size="icon" className="lg:hidden mr-2" onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="customer-menu-toggle">
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </Button>
          <h2 className="text-sm font-semibold text-slate-700">{brandName}</h2>
        </header>
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          <div className="max-w-[1600px] mx-auto fade-in">
            <Outlet />
          </div>
        </main>
        <MobileBottomNav items={CUSTOMER_BOTTOM_NAV} primaryColor={primary} />
      </div>
    </div>
  );
}
